import { useState, useEffect } from 'react'
import api from '../api/client'
import Modal from './Modal'
import Boton from './Boton'
import { PROVINCIAS, esCapitalFederal, matchProvincia } from '../data/argentina'
import CampoLocalidad from './CampoLocalidad'
import CampoDireccion from './CampoDireccion'
import ModalListaVendedores from './ModalListaVendedores'

const TIPOS_DOC = [
  { value: 80, label: 'CUIT' },
  { value: 85, label: 'CUIL' },
  { value: 96, label: 'DNI' },
  { value: 94, label: 'Pasaporte' },
  { value: 99, label: 'Sin identificar' },
]

const TIPOS_RESP = [
  { value: 1.0, label: 'Responsable Inscripto (RI)' },
  { value: 3.1, label: 'Monotributo' },
  { value: 4.0, label: 'Exento' },
  { value: 5.0, label: 'Consumidor Final' },
]

// La ficha llega con tipo_resp como decimal string ("5.0") y nulls: se
// normaliza a lo que entienden los <select> (misma regla que el ABM, sug #18).
function desdeFicha(c) {
  const f = { ...EMPTY }
  for (const k of Object.keys(EMPTY)) if (c[k] != null) f[k] = String(c[k])
  f.tipo_resp = c.tipo_resp != null && c.tipo_resp !== '' ? String(parseFloat(c.tipo_resp)) : ''
  f.vendedor_nombre = ''
  return f
}

const EMPTY = {
  codigo_presea: '', nombre: '', tipo_doc: '', cuit: '', tipo_resp: '',
  domicilio: '', localidad: '', provincia: '', codigo_postal: '',
  email: '', telefono: '', telefono_wa: '', lista_precios: '',
  codigo_vendedor_presea: '', vendedor_nombre: '',
}

/*
 * Alta de cliente — y desde el 26 ago 2026 también EDICIÓN de la ficha, con
 * el mismo formulario (pedido de Martin: el cliente actualizó sus datos en
 * Magento o por teléfono y el vendedor tiene que poder corregir la ficha
 * desde la NV, sin ir al ítem Clientes).
 *
 * Con `cliente` el modal abre en modo edición: guarda con PUT /clientes/{id}
 * (mismo endpoint y validaciones que el ABM — lo que se corrige acá es LA
 * FICHA, no una copia para esta NV) y devuelve la ficha actualizada por
 * `onGuardado` (o `onCreado`, que sigue valiendo para el alta).
 */
export default function ModalAltaCliente({ cliente = null, onClose, onCreado, onGuardado }) {
  const editando = !!cliente?.id
  const [form, setForm] = useState(() => editando ? desdeFicha(cliente) : EMPTY)
  const [saving, setSaving] = useState(false)
  const [errores, setErrores] = useState({})
  const [afipStatus, setAfipStatus] = useState(null)
  const [afipMsg, setAfipMsg] = useState('')
  const [listas, setListas] = useState([])
  const [showVendedores, setShowVendedores] = useState(false)
  // En edición el padrón solo se consulta si el operador cambió el CUIT: la
  // respuesta de ARCA pisa nombre y domicilio, y pasar por el campo sin
  // tocarlo no puede reescribir la ficha.
  const [cuitTocado, setCuitTocado] = useState(false)

  useEffect(() => {
    api.get('/listas-precios').then(({ data }) => {
      const items = Array.isArray(data) ? data : (data.data || [])
      setListas(items)
    }).catch(() => setListas([]))
    // El código de Presea no se pre-asigna: queda vacío ("pendiente") hasta que
    // Presea lo asigne en la primera factura.
  }, [])

  // Editando una ficha con vendedor cargado: resolver el nombre para que el
  // campo no muestre el código pelado (mismo patrón que el ABM Clientes).
  useEffect(() => {
    if (!editando) return
    const cod = cliente.codigo_vendedor_presea
    if (!cod) return
    api.get(`/vendedores/${cod}`)
      .then(({ data }) => setForm(f => String(f.codigo_vendedor_presea) === String(cod) ? { ...f, vendedor_nombre: data.nombre || '' } : f))
      .catch(() => {})
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editando])

  const set = (key) => (e) => setForm(f => ({ ...f, [key]: e.target.value }))
  // DNI (96) ⇒ Consumidor Final por defecto (si no se eligió otra cond. IVA)
  const setTipoDoc = (e) => {
    const v = e.target.value
    setForm(f => ({ ...f, tipo_doc: v, tipo_resp: (String(v) === '96' && !f.tipo_resp) ? '5' : f.tipo_resp }))
  }
  const fieldErr = (key) => errores[key]?.[0]

  // Provincia/localidad canónicas para que el <select> muestre el valor aunque
  // venga en otra capitalización/tildes (p.ej. autocompletado AFIP) — #24.
  const provinciaCanon = matchProvincia(form.provincia)
  const esCaba = esCapitalFederal(provinciaCanon)

  const validarAfip = async () => {
    const cuit = form.cuit.replace(/\D/g, '')
    if (cuit.length !== 11) { setAfipMsg('Ingresá un CUIT de 11 dígitos.'); setAfipStatus('error'); return }
    setAfipStatus('loading')
    setAfipMsg('')
    try {
      const { data } = await api.get(`/afip/contribuyente/${cuit}`)
      // ARCA dice "CIUDAD AUTONOMA BUENOS AIRES" sin localidad; el form usa
      // CAPITAL FEDERAL (y en CABA la localidad se oculta — manda el CP).
      const prov = matchProvincia(data.provincia || '')
      const caba = esCapitalFederal(prov)
      setForm(f => ({
        ...f,
        // 11 dígitos validados en el padrón = es un CUIT: el tipo de
        // documento se completa solo (el modal abre con el campo vacío).
        tipo_doc: '80',
        nombre: data.nombre || f.nombre,
        // La condición fiscal la dice el padrón: define la letra de la
        // factura y las percepciones — no se deja adivinar al operador.
        tipo_resp: data.tipo_resp != null ? String(parseFloat(data.tipo_resp)) : f.tipo_resp,
        domicilio: data.domicilio || f.domicilio,
        localidad: caba ? f.localidad : (data.localidad || f.localidad),
        provincia: prov || f.provincia,
        codigo_postal: data.codigo_postal || f.codigo_postal,
      }))
      setAfipStatus('ok')
      const cond = { RI: 'Resp. Inscripto', MONOTRIBUTO: 'Monotributo', EXENTO: 'Exento', CF: 'Cons. Final' }[data.condicion_iva]
      setAfipMsg(`Validado: ${data.nombre}${cond ? ` · ${cond}` : ''}`)
    } catch (err) {
      setAfipStatus('error')
      setAfipMsg(err.response?.data?.error || 'No se pudo validar en AFIP.')
    }
  }

  // altaPresea=true: además de guardar la ficha, manda el ALTA SOLA a Presea
  // (sin factura) — paso 1 del circuito de la factura proforma: Presea asigna
  // el código y registra la condición fiscal (de ahí salen las percepciones).
  const handleSave = async (e, altaPresea = false) => {
    e.preventDefault()
    if (!form.lista_precios && !editando) {
      setErrores({ lista_precios: ['Seleccioná una lista de precios para el cliente.'] })
      return
    }
    setSaving(true)
    setErrores({})
    try {
      const payload = {
        ...form,
        tipo_doc: form.tipo_doc ? parseInt(form.tipo_doc) : null,
        tipo_resp: form.tipo_resp ? parseFloat(form.tipo_resp) : null,
        lista_precios: form.lista_precios || null,
        localidad: esCaba ? null : (form.localidad || null),
        codigo_postal: form.codigo_postal || null,
        codigo_vendedor_presea: form.codigo_vendedor_presea ? parseInt(form.codigo_vendedor_presea) : null,
        vendedor_nombre: undefined,
      }
      if (editando) {
        // El número de cliente lo asigna Presea: en edición no se toca.
        delete payload.codigo_presea
        const { data } = await api.put(`/clientes/${cliente.id}`, payload)
        // El PUT replica la ficha en Presea: se informa qué pasó allá (la
        // ficha de LGR ya quedó guardada en todos los casos).
        const p = data.presea
        if (p?.enviado && !p.ok) {
          window.alert(`⚠ Los cambios se guardaron en LGR pero NO llegaron a Presea: ${p.error}`)
        } else if (p?.enviado && p.aviso) {
          window.alert(`⚠ Cambios guardados en LGR y en Presea. ${p.aviso}`)
        } else if (p?.enviado) {
          window.alert(`✅ Cambios guardados en LGR y en Presea (cód. ${p.codigo}).`)
        }
        ;(onGuardado || onCreado)(data)
        return
      }
      payload.activo = true
      const { data } = await api.post('/clientes', payload)
      if (altaPresea) {
        try {
          const { data: alta } = await api.post(`/clientes/${data.id}/enviar-alta-presea`)
          window.alert(`✅ Cliente dado de alta en Presea con el código ${alta.codigo_presea}${alta.ya_existia ? ' (ya existía — se actualizó su ficha)' : ''}.`)
          onCreado(alta.cliente || data)
          return
        } catch (err) {
          // La ficha LGR ya quedó creada — avisar el rechazo de Presea y no
          // cerrar el modal (el operador puede corregir y reintentar después).
          const msg = err.response?.data?.errors
            ? Object.values(err.response.data.errors).flat().join(' · ')
            : (err.response?.data?.message || 'Presea no respondió.')
          window.alert(`⚠ El cliente se guardó en LGR pero el alta en Presea falló: ${msg}`)
          onCreado(data)
          return
        }
      }
      onCreado(data)
    } catch (err) {
      setErrores(err.response?.data?.errors || { general: [err.response?.data?.message || 'Error al guardar.'] })
    } finally { setSaving(false) }
  }

  return (
    <Modal title={editando ? 'Editar ficha del cliente' : 'Nuevo Cliente'} onClose={onClose} maxWidth="640px">
      <form onSubmit={handleSave} style={st.form}>
        {errores.general && <div style={st.errorBox}>{errores.general[0]}</div>}

        {editando ? (
          <div style={st.avisoEdicion}>
            Estás corrigiendo <b>la ficha</b> del cliente
            {cliente.codigo_presea ? <> (Nº <code>{cliente.codigo_presea}</code>)</> : ' (número pendiente: lo asigna Presea)'}:
            los cambios valen para todas sus operaciones, no solo para esta nota de venta.
          </div>
        ) : (
          <div>
            <label style={st.label}>Número de cliente</label>
            <input style={st.input} value={form.codigo_presea} onChange={set('codigo_presea')} placeholder="Se asigna al facturar en Presea" />
            <span style={{ ...st.err, color: '#94a3b8', fontWeight: 400 }}>Dejalo vacío: Presea le asigna el número en la primera factura.</span>
            {fieldErr('codigo_presea') && <span style={st.err}>{fieldErr('codigo_presea')}</span>}
          </div>
        )}

        <div style={st.row2}>
          <div style={{ flex: 2 }}>
            <label style={st.label}>Nombre / Razón Social *</label>
            <input style={st.input} value={form.nombre} onChange={set('nombre')} required />
            {fieldErr('nombre') && <span style={st.err}>{fieldErr('nombre')}</span>}
          </div>
          <div style={{ flex: 1 }}>
            <label style={st.label}>Tipo de documento</label>
            <select style={st.input} value={form.tipo_doc} onChange={setTipoDoc}>
              <option value="">— Seleccionar —</option>
              {TIPOS_DOC.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </div>
        </div>

        <div style={st.row2}>
          <div style={{ flex: 1 }}>
            <label style={st.label}>CUIT / Documento{editando ? '' : ' *'}</label>
            <div style={{ display: 'flex', gap: '0.4rem' }}>
              <input style={{ ...st.input, flex: 1 }} value={form.cuit} onChange={e => { set('cuit')(e); setCuitTocado(true); setAfipStatus(null); setAfipMsg('') }} onBlur={() => { if ((cuitTocado || !editando) && form.cuit.replace(/\D/g, '').length === 11 && afipStatus === null) validarAfip() }} placeholder={String(form.tipo_doc) === '80' ? 'XX-XXXXXXXX-X' : ''} required={!editando} autoComplete="off" />
              {(String(form.tipo_doc) === '80' || form.cuit.replace(/\D/g, '').length === 11) && (
                <Boton variante="secondary" sm type="button" onClick={validarAfip} disabled={afipStatus === 'loading'} title="Validar en AFIP">
                  {afipStatus === 'loading' ? '...' : 'AFIP'}
                </Boton>
              )}
            </div>
            {afipMsg && <span style={{ ...st.err, color: afipStatus === 'ok' ? '#166534' : '#dc2626' }}>{afipMsg}</span>}
            {afipStatus === 'ok' && <span style={st.afipBadge}>Validado AFIP</span>}
            {fieldErr('cuit') && <span style={st.err}>{fieldErr('cuit')}</span>}
          </div>
          <div style={{ flex: 1 }}>
            <label style={st.label}>Condición IVA</label>
            <select style={st.input} value={form.tipo_resp} onChange={set('tipo_resp')}>
              <option value="">— Seleccionar —</option>
              {TIPOS_RESP.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </div>
        </div>

        <div>
          <label style={st.label}>Domicilio</label>
          <CampoDireccion
            value={form.domicilio}
            onChange={v => setForm(f => ({ ...f, domicilio: v }))}
            onPlaceSelected={d => setForm(f => ({
              ...f,
              // calle_completa conserva el piso/depto tipeado tras la altura.
              domicilio: d.calle_completa || d.calle || f.domicilio,
              localidad: d.localidad || f.localidad,
              provincia: d.provincia || f.provincia,
              codigo_postal: d.codigo_postal || f.codigo_postal,
            }))}
            inputStyle={st.input}
            placeholder="Calle y número"
          />
        </div>

        <div style={st.row2}>
          <div style={{ flex: 1 }}>
            <label style={st.label}>Provincia *</label>
            <select style={st.input} value={provinciaCanon} onChange={set('provincia')} required={!editando}>
              <option value="">— Seleccionar —</option>
              {PROVINCIAS.map(p => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>
          {!esCaba && (
            <div style={{ flex: 1 }}>
              <label style={st.label}>Localidad</label>
              <CampoLocalidad
                provincia={provinciaCanon}
                value={form.localidad}
                onChange={v => setForm(f => ({ ...f, localidad: v }))}
                inputStyle={st.input}
                cp={form.codigo_postal}
              />
            </div>
          )}
          <div style={{ flex: esCaba ? 1 : 0.5 }}>
            <label style={st.label}>Código postal{esCaba ? ' *' : ''}</label>
            <input style={st.input} value={form.codigo_postal} onChange={set('codigo_postal')} required={esCaba} />
          </div>
        </div>

        <div>
          <label style={st.label}>Email *</label>
          <input style={st.input} type="email" value={form.email} onChange={set('email')} required />
          <span style={{ ...st.err, color: '#94a3b8', fontWeight: 400 }}>Obligatorio: sin email, Presea rechaza la factura del cliente.</span>
          {fieldErr('email') && <span style={st.err}>{fieldErr('email')}</span>}
        </div>

        <div style={st.row2}>
          <div style={{ flex: 1 }}>
            <label style={st.label}>Teléfono{editando ? '' : ' *'}</label>
            <input style={st.input} value={form.telefono} onChange={set('telefono')} required={!editando} autoComplete="off" />
            {fieldErr('telefono') && <span style={st.err}>{fieldErr('telefono')}</span>}
          </div>
          <div style={{ flex: 1 }}>
            <label style={st.label}>WhatsApp</label>
            <input style={st.input} value={form.telefono_wa} onChange={set('telefono_wa')} autoComplete="off" />
          </div>
        </div>

        <div>
          <label style={st.label}>Vendedor (Presea)</label>
          <div style={{ display: 'flex', gap: '0.4rem' }}>
            <input
              style={{ ...st.input, flex: 1, background: '#f8fafc', cursor: 'pointer' }}
              readOnly
              value={form.codigo_vendedor_presea ? `${form.codigo_vendedor_presea}${form.vendedor_nombre ? ` — ${form.vendedor_nombre}` : ''}` : ''}
              placeholder="Sin asignar — elegilo con la lupa"
              onClick={() => setShowVendedores(true)}
            />
            {form.codigo_vendedor_presea && (
              <Boton variante="ghost" sm type="button" title="Quitar vendedor"
                onClick={() => setForm(f => ({ ...f, codigo_vendedor_presea: '', vendedor_nombre: '' }))}>✕</Boton>
            )}
            <Boton variante="secondary" sm type="button" onClick={() => setShowVendedores(true)}>🔍</Boton>
          </div>
          {fieldErr('codigo_vendedor_presea') && <span style={st.err}>{fieldErr('codigo_vendedor_presea')}</span>}
        </div>

        <div>
          <label style={st.label}>Lista de precios{editando ? '' : ' *'}</label>
          <select style={st.input} value={form.lista_precios} onChange={set('lista_precios')} required={!editando}>
            <option value="">— Seleccionar lista —</option>
            {listas.map(l => (
              <option key={l.id} value={l.codigo_presea || l.codigo}>
                {l.nombre} {(l.codigo_presea || l.codigo) ? `(${l.codigo_presea || l.codigo})` : ''}
              </option>
            ))}
          </select>
          {fieldErr('lista_precios') && <span style={st.err}>{fieldErr('lista_precios')}</span>}
        </div>

        <div style={st.actions}>
          <Boton variante="ghost" type="button" onClick={onClose}>Cancelar</Boton>
          {/* Paso 1 del circuito proforma: alta SOLA en Presea (sin factura) —
              asigna el código y registra la condición fiscal del cliente.
              En edición no: la ficha viaja a Presea con la próxima factura. */}
          {!editando && (
            <Boton variante="send" type="button" disabled={saving} onClick={(e) => handleSave(e, true)}
              title="Guarda la ficha y manda el ALTA del cliente a Presea (sin factura): Presea asigna el código y registra la condición fiscal — necesario para calcular percepciones en la proforma">
              → Guardar y dar de alta en Presea
            </Boton>
          )}
          <Boton variante="primary" type="submit" disabled={saving}>
            {saving ? 'Guardando...' : (editando ? 'Guardar cambios en Presea' : 'Crear Cliente')}
          </Boton>
        </div>
      </form>
      {showVendedores && (
        <ModalListaVendedores
          onSelect={(v) => { setForm(f => ({ ...f, codigo_vendedor_presea: String(v.codigo), vendedor_nombre: v.nombre || '' })); setShowVendedores(false) }}
          onClose={() => setShowVendedores(false)}
        />
      )}
    </Modal>
  )
}

const st = {
  form: { display: 'flex', flexDirection: 'column', gap: '0.75rem' },
  row2: { display: 'flex', gap: '0.75rem' },
  label: { display: 'block', fontWeight: '600', fontSize: '0.8125rem', color: '#374151', marginBottom: '0.2rem' },
  input: { width: '100%', padding: '0.5rem 0.75rem', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '0.875rem', boxSizing: 'border-box' },
  err: { color: '#dc2626', fontSize: '0.75rem', display: 'block', marginTop: '2px' },
  errorBox: { background: '#fee2e2', color: '#991b1b', padding: '0.5rem 0.75rem', borderRadius: '6px', fontSize: '0.875rem' },
  actions: { display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginTop: '0.25rem' },
  avisoEdicion: { background: '#fffbeb', border: '1px solid #fde68a', color: '#78350f', padding: '0.5rem 0.75rem', borderRadius: '6px', fontSize: '0.8125rem' },
  afipBadge: { display: 'inline-block', marginTop: '2px', background: '#dcfce7', color: '#166534', fontSize: '0.7rem', fontWeight: '700', padding: '1px 6px', borderRadius: '10px' },
}
